import React, { useState, useEffect } from 'react';
import DashboardLayout from "../layouts/DashboardLayout";
import InfoModal from "../layouts/InfoModal";
import PaginatedTableContainer from "../containers/PaginatedTableContainer";
import { getAllEvents, getEventAdmin, createEvent, updateEvent, deleteEvent } from "../../api/AdminApi";
import { uploadImage } from "../../../mobile/api/ImageApi";

const EventManagementPage = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // 모달 관련 상태
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);

    // 탭 관련 상태
    const [activeTab, setActiveTab] = useState('info');

    // 등록/수정 폼
    const [formTitle, setFormTitle] = useState('');
    const [formContent, setFormContent] = useState('');
    const [formImageId, setFormImageId] = useState(null);
    const [uploadedFileName, setUploadedFileName] = useState('');
    const [imageUploading, setImageUploading] = useState(false);

    const fetchEvents = async (page = 0) => {
        setLoading(true);
        try {
            const response = await getAllEvents(page, 20);
            setEvents(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
        } catch (error) {
            console.error('이벤트 목록 조회 실패:', error);
            alert('이벤트 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = async (event) => {
        setModalLoading(true);
        setModalOpen(true);
        setIsCreateMode(false);
        setActiveTab('info'); // 기본 탭으로 설정

        try {
            const eventDetail = await getEventAdmin(event.id);
            setSelectedEvent(eventDetail);
            setFormTitle(eventDetail.title);
            setFormContent(eventDetail.content);
            setFormImageId(eventDetail.imageId); // 기존 imageId 유지
            setUploadedFileName('');
        } catch (error) {
            console.error('이벤트 상세 조회 실패:', error);
            alert('이벤트 정보를 불러오는데 실패했습니다.');
            setModalOpen(false);
        } finally {
            setModalLoading(false);
        }
    };

    const handleCreateClick = () => {
        setSelectedEvent(null);
        setFormTitle('');
        setFormContent('');
        setFormImageId(null);
        setUploadedFileName('');
        setIsCreateMode(true);
        setActiveTab('edit'); // 등록 모드에서는 바로 수정 탭으로
        setModalOpen(true);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setImageUploading(true);
        try {
            const response = await uploadImage(file);
            setFormImageId(response.id);
            setUploadedFileName(file.name);
        } catch (error) {
            console.error('이미지 업로드 실패:', error);
            alert('이미지 업로드에 실패했습니다.');
        } finally {
            setImageUploading(false);
        }
    };

    const handleImageRemove = () => {
        setFormImageId(null);
        setUploadedFileName('');
        // 파일 input 초기화
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleSubmit = async () => {
        if (!formTitle.trim() || !formContent.trim()) {
            alert('제목과 내용을 모두 입력해주세요.');
            return;
        }

        try {
            if (isCreateMode) {
                await createEvent(formTitle, formContent, formImageId);
                alert('이벤트가 등록되었습니다.');
            } else {
                await updateEvent(selectedEvent.id, formTitle, formContent, formImageId);
                alert('이벤트가 수정되었습니다.');
            }
            setModalOpen(false);
            fetchEvents(currentPage);
        } catch (error) {
            console.error('이벤트 저장 실패:', error);
            alert('이벤트 저장에 실패했습니다.');
        }
    };

    const handleDelete = async () => {
        const confirmed = window.confirm(`정말로 "${selectedEvent.title}" 이벤트를 삭제하시겠습니까? 이 동작은 되돌릴 수 없습니다.`);
        if (!confirmed) return;

        try {
            await deleteEvent(selectedEvent.id);
            alert('이벤트가 삭제되었습니다.');
            setModalOpen(false);
            fetchEvents(currentPage);
        } catch (error) {
            console.error('이벤트 삭제 실패:', error);
            alert('이벤트 삭제에 실패했습니다.');
        }
    };

    useEffect(() => {
        fetchEvents();
    }, []);

    const handlePageChange = (page) => {
        if (page >= 0 && page < totalPages) {
            setCurrentPage(page);
            fetchEvents(page);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('ko-KR');
    };

    // 테이블 컬럼 정의 (Event 엔터티 기준)
    const columns = [
        { header: 'id', field: 'id', minWidth: 'min-w-12' },
        { header: 'title', field: 'title', minWidth: 'min-w-48' },
        {
            header: 'content',
            field: 'content',
            minWidth: 'min-w-32',
            render: (event) => {
                if (!event.content) return '';
                return event.content.length > 30 ? `${event.content.substring(0, 30)}...` : event.content;
            }
        },
        {
            header: 'imageId',
            field: 'imageId',
            minWidth: 'min-w-16',
            render: (event) => event.imageId || ''
        },
        {
            header: 'createdAt',
            field: 'createdAt',
            minWidth: 'min-w-32',
            render: (event) => formatDateTime(event.createdAt)
        },
        {
            header: 'updatedAt',
            field: 'updatedAt',
            minWidth: 'min-w-32',
            render: (event) => formatDateTime(event.updatedAt)
        }
    ];

    return (
        <DashboardLayout>
            <div className="p-3">
                <h1 className="text-lg font-bold mb-3">이벤트 관리</h1>

                {/* 등록 버튼 */}
                <div className="mb-3">
                    <button
                        onClick={handleCreateClick}
                        className="px-3 py-1 bg-gray-200 border border-gray-400 hover:bg-gray-300 text-xs"
                    >
                        이벤트 등록
                    </button>
                </div>

                {/* 상단 정보 */}
                <div className="mb-2 text-xs text-gray-600">
                    총 {totalElements}개 | {currentPage + 1}/{totalPages} 페이지
                </div>

                {/* 페이지네이션 테이블 */}
                <PaginatedTableContainer
                    columns={columns}
                    data={events}
                    loading={loading}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowClick={handleRowClick}
                />

                {/* 이벤트 상세/등록/수정 모달 */}
                <InfoModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    title={isCreateMode ? '이벤트 등록' : `이벤트 정보 - ${selectedEvent?.title || ''}`}
                    width="w-[700px]"
                >
                    {modalLoading ? (
                        <div className="text-center py-4">이벤트 정보를 불러오는 중...</div>
                    ) : (
                        <div className="text-xs">
                            {/* 탭 (등록 모드가 아닌 경우에만) */}
                            {!isCreateMode && (
                                <div className="mb-4">
                                    <div className="flex border-b border-gray-400">
                                        <button
                                            onClick={() => handleTabChange('info')}
                                            className={`px-3 py-2 text-xs border-l border-t border-r border-gray-400 ${
                                                activeTab === 'info' 
                                                    ? 'bg-white border-b-white -mb-px relative z-10' 
                                                    : 'bg-gray-200 border-b-gray-400'
                                            }`}
                                        >
                                            이벤트 정보
                                        </button>
                                        <button
                                            onClick={() => handleTabChange('edit')}
                                            className={`px-3 py-2 text-xs border-t border-r border-gray-400 ${
                                                activeTab === 'edit' 
                                                    ? 'bg-white border-b-white -mb-px relative z-10' 
                                                    : 'bg-gray-200 border-b-gray-400'
                                            }`}
                                        >
                                            이벤트 수정
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* 탭 내용 */}
                            {(isCreateMode || activeTab === 'edit') ? (
                                /* 이벤트 등록/수정 폼 */
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="font-bold mb-2 text-sm">{isCreateMode ? '이벤트 등록' : '이벤트 수정'}</h3>
                                        <div className="space-y-2">
                                            <div>
                                                <label className="block mb-1 text-xs font-medium">제목</label>
                                                <input
                                                    type="text"
                                                    value={formTitle}
                                                    onChange={(e) => setFormTitle(e.target.value)}
                                                    className="w-full px-2 py-1 border border-gray-300 text-xs"
                                                    placeholder="이벤트 제목을 입력하세요"
                                                />
                                            </div>
                                            <div>
                                                <label className="block mb-1 text-xs font-medium">내용</label>
                                                <textarea
                                                    value={formContent}
                                                    onChange={(e) => setFormContent(e.target.value)}
                                                    rows="10"
                                                    className="w-full px-2 py-1 border border-gray-300 text-xs"
                                                    placeholder="이벤트 내용을 입력하세요"
                                                />
                                            </div>
                                            <div>
                                                <label className="block mb-1 text-xs font-medium">이미지</label>
                                                <div className="space-y-2">
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={handleImageUpload}
                                                            className="text-xs"
                                                            disabled={imageUploading}
                                                        />
                                                        {imageUploading && <span className="text-xs text-gray-500">업로드 중...</span>}
                                                    </div>
                                                    {formImageId && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-green-600">
                                                                {uploadedFileName ? '이미지 업로드됨' : '기존 이미지'}
                                                            </span>
                                                            <button
                                                                onClick={handleImageRemove}
                                                                className="px-2 py-1 bg-red-200 border border-gray-400 hover:bg-red-300 text-red-800 text-xs"
                                                            >
                                                                이미지 삭제
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={handleSubmit}
                                                    className="px-3 py-1 bg-blue-200 border border-gray-400 hover:bg-blue-300"
                                                >
                                                    {isCreateMode ? '등록' : '수정'}
                                                </button>
                                                {!isCreateMode && (
                                                    <button
                                                        onClick={handleDelete}
                                                        className="px-3 py-1 bg-red-200 border border-gray-400 hover:bg-red-300 text-red-800"
                                                    >
                                                        삭제
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* 이벤트 정보 탭 */
                                selectedEvent && (
                                    <div className="space-y-4">
                                        {/* 이벤트 정보 테이블 */}
                                        <div>
                                            <h3 className="font-bold mb-2 text-sm">이벤트 정보</h3>
                                            <table className="w-full border border-gray-400">
                                                <tbody>
                                                    <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100 w-24">id</td><td className="border border-gray-400 px-2 py-1">{selectedEvent.id}</td></tr>
                                                    <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">title</td><td className="border border-gray-400 px-2 py-1">{selectedEvent.title}</td></tr>
                                                    <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">content</td><td className="border border-gray-400 px-2 py-1">{selectedEvent.content || ''}</td></tr>
                                                    <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">imageId</td><td className="border border-gray-400 px-2 py-1">{selectedEvent.imageId || ''}</td></tr>
                                                    <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">createdAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedEvent.createdAt)}</td></tr>
                                                    <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">updatedAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedEvent.updatedAt)}</td></tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* 이벤트 이미지 */}
                                        {selectedEvent.imageUrl && (
                                            <div>
                                                <h4 className="font-bold mb-2 text-sm">이벤트 이미지</h4>
                                                <img
                                                    src={selectedEvent.imageUrl}
                                                    alt="이벤트 이미지"
                                                    className="max-w-full max-h-64 border border-gray-300"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </InfoModal>
            </div>
        </DashboardLayout>
    );
};

export default EventManagementPage;
